import { AppStateEnum } from '../../../../../main/types/app-state.types';

import { CharacterBubbleAnimation } from '../../../../constants/character-bubble-animations';
import { WordFindingErrors } from '../../../../constants/errors';
import { STORY_WORD_TAP_RECOGNITION_MESSAGES } from '../../../../constants/story-word-tap';
import { selectCurrentLessonTaskStepKey } from '../../../../selectors/current-lesson-task-step-key.selector';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector';
import { triggerCharacterBubbleAnimationThunk } from '../../../../slices/app-state/thunks/trigger-character-bubble-animation.thunk';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice';
import { setScriptReplacement } from '../../../../slices/scripts/scripts.slice';
import { ScriptReplacementKey } from '../../../../slices/scripts/scripts.slice.types';

import { getTappableStoryWordUtil } from '../../../managers/tappable-story-word.util';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler';

export class StoryAskToTapWordStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  private static _instance: StoryAskToTapWordStateChangeHandlerUtil;
  private lastTappedWord: string | null = null;

  private constructor(private readonly tappableStoryWordUtil = getTappableStoryWordUtil()) {
    super();
  }

  public static get instance(): StoryAskToTapWordStateChangeHandlerUtil {
    if (!StoryAskToTapWordStateChangeHandlerUtil._instance) {
      StoryAskToTapWordStateChangeHandlerUtil._instance = new StoryAskToTapWordStateChangeHandlerUtil();
    }
    return StoryAskToTapWordStateChangeHandlerUtil._instance;
  }

  protected getHandlerName(): string {
    return 'StoryAskToTapWord';
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);
    this.incorrectAttemptsUtil.handleStartStep();
    await this.handleStartStep();
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log(`${this.getHandlerName()} onRetry`);
    await this.handleStartStep();
  }

  protected override async onAwaitingTap(): Promise<void> {
    console.log(`${this.getHandlerName()} onAwaitingTap`);
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();

    dispatch(setBallIsVisible(false));

    const { step } = selectCurrentLessonTaskStep(state);
    if (!step.target) {
      this.handleError(new Error(WordFindingErrors.INVALID_STEP_TARGET));
      return;
    }

    const clickedWord = await this.tappableStoryWordUtil.waitForClick();
    const expectedWord = step.target;

    if (clickedWord === expectedWord) {
      this.lastTappedWord = clickedWord;
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Correct, blockAnimation: true }));
    } else {
      dispatch(
        setScriptReplacement({
          key: ScriptReplacementKey.IncorrectWord,
          value: clickedWord,
        })
      );

      this.lastTappedWord = clickedWord;
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect, blockAnimation: true }));
    }
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    await this.handleCorrect({
      assuming: false,
      answer: this.lastTappedWord ?? STORY_WORD_TAP_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    await this.handleCorrect({
      assuming: true,
      answer: this.lastTappedWord ?? STORY_WORD_TAP_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);
    await this.handleIncorrect(this.lastTappedWord ?? STORY_WORD_TAP_RECOGNITION_MESSAGES.NO_RESPONSE);
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const state = await this.reduxStoreUtil.getRootState();
    const stepKey = selectCurrentLessonTaskStepKey(state);
    if (this.lessonsUtil.isFinalStep(stepKey)) {
      await dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.SHAKE));
      await this.audioPlayerUtil.speakStep();
      await this.audioPlayerUtil.waitSpeakCompletion();
      dispatch(setBallIsVisible(true));
    } else {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.MoveToNextStep }));
    }
  }

  private async handleStartStep(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    this.lastTappedWord = null;

    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    await dispatch(updateAppStateThunk({ state: AppStateEnum.AwaitingTap, blockAnimation: true })).unwrap();
  }
}

export const getStoryAskToTapWordStateChangeHandlerUtil = (): StoryAskToTapWordStateChangeHandlerUtil =>
  StoryAskToTapWordStateChangeHandlerUtil.instance;
